/**
 * HamburgerMenu Component
 * Manages multi-level navigation menu interactions
 * Level 1: Main items (Vårt erbjudande, Operations, etc.)
 * Level 2: Column headings (child items)
 * Level 3: Grand-child items (links)
 */

interface IMenuLevel {
  trigger: HTMLElement;
  content: HTMLElement;
}

export class HamburgerMenu {
  private hamburgerMenu: HTMLElement | null;
  private mainItems: IMenuLevel[] = [];
  private childItems: IMenuLevel[] = [];

  constructor() {
    this.hamburgerMenu = document.getElementById("hamburger-menu");

    if (this.hamburgerMenu) {
      this.initMainMenu();
      this.initChildMenu();
      this.openCurrentPage();
    }
  }

  /**
   * Initialize main menu level (Level 1: Main items)
   */
  private initMainMenu(): void {
    const mainLinks = this.hamburgerMenu?.querySelectorAll(
      ".hamburger-nav-link",
    );

    mainLinks?.forEach((link: Element) => {
      const linkElement = link as HTMLElement;
      const menuItem = linkElement.closest(".hamburger-nav-item");
      const dropdown = menuItem?.querySelector(
        ".hamburger-dropdown",
      ) as HTMLElement;

      if (dropdown) {
        linkElement.addEventListener("click", (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleMainMenu(linkElement);
        });

        this.mainItems.push({
          trigger: linkElement,
          content: dropdown,
        });
      }
    });
  }

  /**
   * Initialize child menu level (Level 2: Column headings)
   */
  private initChildMenu(): void {
    const childHeadings = this.hamburgerMenu?.querySelectorAll(
      ".hamburger-child-heading",
    );

    childHeadings?.forEach((heading: Element) => {
      const headingElement = heading as HTMLElement;
      const grandchildList = headingElement.nextElementSibling as HTMLElement;

      if (
        grandchildList &&
        grandchildList.classList.contains("hamburger-grand-child-list")
      ) {
        headingElement.addEventListener("click", (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleChildMenu(headingElement);
        });

        this.childItems.push({
          trigger: headingElement,
          content: grandchildList,
        });
      }
    });
  }

  /**
   * Open the current page's dropdown and grandchild menu
   */
  private openCurrentPage(): void {
    // Find the nav item that contains the current page
    const currentNavItem = this.hamburgerMenu?.querySelector(
      ".hamburger-nav-item.is-current",
    ) as HTMLElement;

    if (!currentNavItem) return;

    // Open the main dropdown if it exists
    const mainButton = currentNavItem.querySelector(
      ".hamburger-nav-link",
    ) as HTMLElement;
    const dropdown = currentNavItem.querySelector(
      ".hamburger-dropdown",
    ) as HTMLElement;

    if (mainButton && dropdown) {
      dropdown.removeAttribute("hidden");
      mainButton.classList.add("active");
    }

    // Open any child dropdowns that contain current pages
    // Look for columns that have the active class OR contain a link with active class
    const childItems = currentNavItem.querySelectorAll(
      ".hamburger-child-item",
    );

    childItems.forEach((childItem: Element) => {
      const childElement = childItem as HTMLElement;
      const heading = childElement.querySelector(
        ".hamburger-child-heading",
      ) as HTMLElement;
      const grandchildList = heading?.nextElementSibling as HTMLElement;

      // Check if this child item has active class OR contains a link with active class
      const hasActiveClass = childElement.classList.contains("active");
      const hasActiveLink = childElement.querySelector("a.active") !== null;

      if ((hasActiveClass || hasActiveLink) && heading && grandchildList) {
        heading.setAttribute("aria-expanded", "true");
        heading.classList.add("active");
        grandchildList.removeAttribute("hidden");
      }
    });
  }

  /**
   * Toggle main menu (Level 1)
   */
  private toggleMainMenu(trigger: HTMLElement): void {
    const menuItem = trigger.closest(".hamburger-nav-item");
    const dropdown = menuItem?.querySelector(
      ".hamburger-dropdown",
    ) as HTMLElement;

    if (!dropdown) return;

    const isHidden = dropdown.hasAttribute("hidden");

    // Close all other main menu items
    this.mainItems.forEach((item) => {
      if (item.trigger !== trigger) {
        item.content.setAttribute("hidden", "");
        item.trigger.classList.remove("active");
      }
    });

    // Toggle current main menu
    if (isHidden) {
      dropdown.removeAttribute("hidden");
      trigger.classList.add("active");
    } else {
      dropdown.setAttribute("hidden", "");
      trigger.classList.remove("active");
    }
  }

  /**
   * Toggle child menu (Level 2)
   */
  private toggleChildMenu(trigger: HTMLElement): void {
    const grandchildList = trigger.nextElementSibling as HTMLElement;

    if (!grandchildList) return;

    const isExpanded = trigger.getAttribute("aria-expanded") === "true";
    const isHidden = grandchildList.hasAttribute("hidden");

    if (isHidden) {
      trigger.setAttribute("aria-expanded", "true");
      trigger.classList.add("active");
      grandchildList.removeAttribute("hidden");
    } else {
      trigger.setAttribute("aria-expanded", "false");
      trigger.classList.remove("active");
      grandchildList.setAttribute("hidden", "");
    }
  }
}
